<script setup lang="ts">
import i18n from '@/localisation/i18n';
import { useEncrypterService } from '@/modules/common/services/encrypter-service';
import { useSnackbarManager } from '@/modules/snackbar/services/snackbar-manager';
import { useRouter } from '@/router/router';
import { useAuthStore } from '@/store/auth-store';
import { LoginSalt } from '@/utils/helpers/rest';
import { i18nServerMessage } from '@/utils/helpers/rest-response';
import axios, { AxiosError } from 'axios';
import { computed, onMounted, shallowRef } from 'vue';
import type { Dictionary } from 'vue-router/types/router';
import { store as vuex } from '@/store/store';
import { isEmail, required, useRules } from '@/composables/vuetify-validation';

const router = useRouter();
const snackbar = useSnackbarManager();
const encryptionService = useEncrypterService();
const authStore = useAuthStore();

const props = withDefaults(defineProps<{ email?: string; message?: string; error?: string }>(), {
  email: '',
  message: '',
  error: '',
});

const symmetricEncrypter = computed(() => encryptionService.getSymmetricEncrypter());
const browserIsSupported = shallowRef(false);
const tfaEnabled = computed<boolean>(() => vuex.getters.tfaEnabled);
const clientConfig = computed(() => vuex.state.clientConfig!);

const browserError = shallowRef('');
const emailAddress = shallowRef(props.email);
const password = shallowRef('');
const loginMessage = shallowRef(props.message);
const loginError = shallowRef(i18n.t(props.error).toString());
const revealPassword = shallowRef(false);
const isProcessing = shallowRef(false);

onMounted(async () => {
  browserIsSupported.value = await encryptionService.isCryptoSupported();

  // Modern browsers have support for windows.crypto.subtle
  // NOTE: website must be running secured! (localhost or https with valid certificate)
  if (!browserIsSupported.value) {
    browserError.value = i18n.tc('encrypt-function-missing');
  }
});

const onSubmit = () =>
  validateAndSubmit(async function (): Promise<void> {
    // run validation
    if (isProcessing.value) return;
    isProcessing.value = true;

    loginError.value = '';
    loginMessage.value = '';

    try {
      // get salt (for the email entered) from the server
      let loginSalt: Buffer;
      try {
        const { data } = await axios.get(`/api/1/login-salt/${emailAddress.value}`);
        loginSalt = Buffer.from((data as LoginSalt).salt, 'base64');
      } catch (e) {
        loginError.value = i18nServerMessage(e as AxiosError);
        return;
      }

      // hash password with login salt to submit to server
      const hashedPassword = await symmetricEncrypter.value.hashPassword(password.value, loginSalt);

      const result = await authStore.login({
        email: emailAddress.value,
        password: hashedPassword,
      });

      // already handled within login
      if (result === undefined) return;

      if (result.success) {
        loginMessage.value = 'Success';
        advise2FA();

        // auth-store.ts will redirect to the homepage via a jwt.value watcher
      } else {
        loginError.value = i18n.t(result.error, result.details) as string;
      }
    } finally {
      isProcessing.value = false;
    }
  });

function lostPassword(): void {
  redirectLogin('lost-password', { lostEmail: emailAddress.value });
}

function redirectLogin(pageName: string, pageParams: Dictionary<string>): void {
  void router.push({
    name: pageName,
    params: pageParams,
  });
}

function advise2FA() {
  if (!tfaEnabled.value) {
    snackbar.show({
      message: i18n.t('loginRegisterDevice.text') as string,
      actionText: i18n.t('loginRegisterDevice.button') as string,
      timeout: 3000,
      onAction: () => router.push({ name: 'settings.register2fa' }),
    });
  }
}

const { rules, validateAndSubmit, validate } = useRules<{ emailAddress: string; password: string }>(
  {
    emailAddress: {
      rules: [
        (v, { isSubmitting, isTargetedField }) =>
          (!isSubmitting && !isTargetedField) || required(v, 'Please enter an email.'),
        (v, { isSubmitting, isTargetedField, wasInvalid }) =>
          (!isSubmitting && !isTargetedField && !wasInvalid) ||
          isEmail(v, 'Please enter a valid email.'),
      ],
    },
    password: {
      rules: [
        // allow for spaces in passwords, `reuired` won't work here
        (v) => !!v || `Please enter a password.'`,
      ],
    },
  }
);
</script>

<!-- eslint-disable vue/no-v-html -->
<template>
  <VDialog
    content-class="au-popup-dialog"
    max-width="600px"
    no-click-animation
    overlay-color="secondary"
    overlay-opacity="0.80"
    persistent
    :value="true"
  >
    <VForm novalidate @submit.prevent="onSubmit">
      <VCard>
        <VCardTitle v-if="clientConfig">
          <span class="headline">
            {{ $t('loginTitle', { title: clientConfig.systemTitleShort }) }}
          </span>
          <span v-if="clientConfig.systemEnv !== 'prod'" class="system-env text--secondary">
            {{ clientConfig.systemEnv }}
          </span>
        </VCardTitle>
        <VCardText>
          <VContainer>
            <VRow>
              <VCol v-if="browserIsSupported" class="message-spacer" cols="12">
                <VAlert v-if="loginError" dense type="error">
                  {{ loginError }}
                </VAlert>
                <VAlert v-else-if="loginMessage" dense type="success">
                  {{ $t(loginMessage, { emailAddress: email }) }}
                </VAlert>
              </VCol>
              <VCol v-else>
                <VAlert dense type="error">
                  <span v-html="browserError"></span>
                </VAlert>
              </VCol>
            </VRow>
            <VRow v-if="browserIsSupported">
              <VCol cols="12">
                <VTextField
                  v-model="emailAddress"
                  autocomplete="username email"
                  autofocus
                  v-bind="rules.emailAddress"
                  :label="$t('loginEmail')"
                  @blur="validate('emailAddress')"
                >
                </VTextField>
              </VCol>
              <VCol cols="12">
                <VTextField
                  v-model="password"
                  :append-icon="revealPassword ? 'mdi-eye' : 'mdi-eye-off'"
                  autocomplete="current-password"
                  counter
                  v-bind="rules.password"
                  :label="$t('loginPassword')"
                  required
                  :type="revealPassword ? 'text' : 'password'"
                  @blur="validate('password')"
                  @click:append="revealPassword = !revealPassword"
                  @input="validate('password')"
                ></VTextField>
              </VCol>
            </VRow>
          </VContainer>
        </VCardText>
        <VCardActions v-if="browserIsSupported">
          <VBtn color="secondary" text @click="lostPassword">
            {{ $t('loginForgotPassword') }}
          </VBtn>
          <VSpacer />
          <VBtn
            color="primary"
            :disabled="isProcessing"
            :loading="isProcessing"
            min-width="150"
            type="submit"
          >
            <v-icon left>mdi-login</v-icon>
            {{ $t('loginButton') }}
          </VBtn>
        </VCardActions>
        <VCardActions v-else>
          <VSpacer />
          <VBtn color="primary" min-width="120" to="/">
            {{ $t('close') }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VForm>
  </VDialog>
</template>

<style lang="scss" scoped>
.system-env {
  text-transform: uppercase;
  margin-top: 2px;
  margin-left: 4px;
}
</style>
